import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import '../styles/page-layout.css';
import '../styles/error-pages.css';

interface MaintenancePageProps {
  estimatedEndTime?: Date;
  maintenanceReason?: string;
}

const MaintenancePage: React.FC<MaintenancePageProps> = ({ 
  estimatedEndTime, 
  maintenanceReason = "Geplante Wartungsarbeiten"
}) => {
  const [timeRemaining, setTimeRemaining] = useState<string>('');

  useEffect(() => {
    if (!estimatedEndTime) return;

    const updateCountdown = () => {
      const now = new Date().getTime();
      const end = estimatedEndTime.getTime();
      const difference = end - now;

      if (difference > 0) {
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        setTimeRemaining(`${hours}h ${minutes}m`);
      } else {
        setTimeRemaining('Wartung sollte beendet sein');
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [estimatedEndTime]);

  return (
    <Layout
      title="Wartungsarbeiten | Stimm.DEV"
      description="Unsere Website wird derzeit gewartet. Wir sind bald wieder für Sie da."
      keywords="Wartung, Maintenance, Temporär nicht verfügbar"
    >
      <section className="wrapper">
        <div className="page-container" style={{ textAlign: 'center', padding: '4rem 2rem' }}>
          <div style={{ maxWidth: '600px', margin: '0 auto' }}>
            <div style={{
              fontSize: '4rem',
              marginBottom: '2rem'
            }} className="pulse-animation">
              🔧
            </div>
            
            <h1 style={{ 
              fontSize: '3rem', 
              fontWeight: '800', 
              background: 'linear-gradient(135deg, var(--primary-color), var(--secondary-color))',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              marginBottom: '1rem'
            }}>
              Wartungsarbeiten
            </h1>
            
            <h2 style={{ 
              fontSize: '1.5rem', 
              marginBottom: '2rem',
              color: 'var(--text-color)',
              fontWeight: '400'
            }}>
              {maintenanceReason}
            </h2>
            
            <p style={{ 
              fontSize: '1.1rem', 
              color: 'var(--text-muted)',
              marginBottom: '3rem',
              lineHeight: '1.6'
            }}>
              Wir führen derzeit wichtige Wartungsarbeiten durch, um Ihnen 
              ein noch besseres Erlebnis zu bieten. Wir sind bald wieder für Sie da!
            </p>

            {estimatedEndTime && timeRemaining && (
              <div style={{
                padding: '2rem',
                background: 'var(--primary-color-light)',
                borderRadius: '1rem',
                marginBottom: '3rem',
                border: '1px solid var(--primary-color)'
              }}>
                <h3 style={{ 
                  color: 'var(--text-color)',
                  marginBottom: '1rem',
                  fontSize: '1.2rem'
                }}>
                  Geschätzte Restzeit
                </h3>
                <div style={{
                  fontSize: '2rem',
                  fontWeight: 'bold',
                  color: 'var(--primary-color)',
                  fontFamily: 'monospace'
                }}>
                  {timeRemaining}
                </div>
              </div>
            )}

            <div style={{ 
              display: 'flex', 
              gap: '1rem', 
              justifyContent: 'center',
              flexWrap: 'wrap',
              marginBottom: '3rem'
            }}>
              <button 
                onClick={() => window.location.reload()}
                className="primary-button"
                style={{ 
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                <i className="fas fa-sync-alt" style={{ marginRight: '0.5rem' }}></i>
                Status prüfen
              </button>
              
              <a 
                href="mailto:hello@stimm.dev"
                className="secondary-button"
                style={{ textDecoration: 'none' }}
              >
                <i className="fas fa-envelope" style={{ marginRight: '0.5rem' }}></i>
                Kontakt
              </a>
            </div>

            <div style={{ 
              padding: '2rem',
              background: 'var(--card-background)',
              borderRadius: '1rem',
              border: '1px solid var(--border-color)',
              textAlign: 'left'
            }}>
              <h3 style={{ marginBottom: '1.5rem', color: 'var(--text-color)' }}>
                Was passiert gerade?
              </h3>
              
              <div style={{ 
                display: 'grid',
                gap: '1rem',
                marginBottom: '2rem'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '1rem',
                  background: 'var(--success-color-light)',
                  borderRadius: '0.5rem',
                  borderLeft: '4px solid var(--success-color)'
                }}>
                  <i className="fas fa-server" style={{ 
                    marginRight: '1rem', 
                    color: 'var(--success-color)',
                    fontSize: '1.2rem'
                  }}></i>
                  <div>
                    <strong style={{ color: 'var(--text-color)' }}>
                      Server-Updates
                    </strong>
                    <p style={{ margin: '0.25rem 0 0 0', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                      Installation wichtiger Sicherheitsupdates
                    </p>
                  </div>
                </div>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '1rem',
                  background: 'var(--warning-color-light)',
                  borderRadius: '0.5rem',
                  borderLeft: '4px solid var(--warning-color)'
                }}>
                  <i className="fas fa-database" style={{ 
                    marginRight: '1rem', 
                    color: 'var(--warning-color)',
                    fontSize: '1.2rem'
                  }}></i>
                  <div>
                    <strong style={{ color: 'var(--text-color)' }}>
                      Datenbank-Optimierung
                    </strong>
                    <p style={{ margin: '0.25rem 0 0 0', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                      Verbesserung der Ladezeiten
                    </p>
                  </div>
                </div>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '1rem',
                  background: 'var(--info-color-light)',
                  borderRadius: '0.5rem',
                  borderLeft: '4px solid var(--info-color)'
                }}>
                  <i className="fas fa-shield-alt" style={{ 
                    marginRight: '1rem', 
                    color: 'var(--info-color)',
                    fontSize: '1.2rem'
                  }}></i>
                  <div>
                    <strong style={{ color: 'var(--text-color)' }}>
                      Sicherheits-Checks
                    </strong>
                    <p style={{ margin: '0.25rem 0 0 0', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                      Überprüfung aller Systeme
                    </p>
                  </div>
                </div>
              </div>

              <div style={{ 
                padding: '1rem',
                background: 'var(--primary-color-light)',
                borderRadius: '0.5rem',
                borderLeft: '4px solid var(--primary-color)'
              }}>
                <strong style={{ color: 'var(--text-color)' }}>
                  Bleiben Sie informiert
                </strong>
                <p style={{ margin: '0.5rem 0 0 0', color: 'var(--text-muted)' }}>
                  Folgen Sie uns auf{' '}
                  <a 
                    href="https://twitter.com/stimmdev" 
                    style={{ color: 'var(--primary-color)', textDecoration: 'none' }}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Twitter
                  </a>
                  {' '}für Live-Updates oder kontaktieren Sie uns direkt unter{' '}
                  <a 
                    href="mailto:support@stimm.dev" 
                    style={{ color: 'var(--primary-color)', textDecoration: 'none' }}
                  >
                    support@stimm.dev
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default MaintenancePage;
